import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { assignSentences } from "@store/thunks/dashboardThunk";
import { fetchSentencesForTranslator } from "@store/thunks/translationThunk";
import { BASE_URL, getStatusStyle, restrictSpecialCharacters, restrictSpecialCharactersMultilingual } from "@utils/constants";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import GenericTable from "@components/tables/GenericTable";
import { openModal, closeModal } from "@store/slices/modalSlice";
import { clearSentences } from "@/store/slices/dashboardSlice";
import apiClient from "@/services/api/apiClient";
import { useNavigate } from "react-router-dom";

const SentenceTranslationTable = () => {
  const dispatch = useDispatch();
  const { sentences } = useSelector((store) => store.dashboard);
  const fileLanguage = useSelector((store) => store.fileLanguage);
  const {targetLanguageName} = useSelector((store) => store.fileLanguage);
  const {fileName} = useSelector((store) => store.fileMeta);
  const translatorUserId = useSelector((store) => store.auth.userId);
  const token = useSelector((store) => store.auth.token);
  const [selectedSentences, setSelectedSentences] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [savedStatus, setSavedStatus] = useState({});
  const [clickReject, setClickReject] = useState(false);
  const [commitStatus, setCommitStatus] = useState({});
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [rejectedTab, setRejectedTab] = useState("me");
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectingSentence, setRejectingSentence] = useState(null);
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });
  const [isCommitModalOpen, setIsCommitModalOpen] = useState({ isOpen: false, sentenceId: null });
  const translationsRef = useRef({});
  const sourceRef = useRef({});
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const navigate = useNavigate();
  const [isFinalCommitted, setIsFinalCommitted] = useState(false);

  const fileStatus = useSelector((store) => store.translator.fileStatus);

  useEffect(() => {
    translationsRef.current = {};
    sourceRef.current = {};
  }, [clickReject]);

  useEffect(() => {
    console.log(fileLanguage);
    if (fileLanguage?.fileId && fileLanguage?.targetLanguageId) {
      console.log("🔄 Fetching sentences with:", {
        fileId: fileLanguage.fileId,
        sourceId: fileLanguage.sourceLanguageId,
        targetId: fileLanguage.targetLanguageId,
        translatorUserId,
        pageIndex,
        pageSize,
      });
      dispatch(clearSentences()); // Clear sentences before fetching
      dispatch(
        fetchSentencesForTranslator(
          fileLanguage.fileId,
          fileLanguage.sourceLanguageId || 2,
          fileLanguage.targetLanguageId,
          translatorUserId,
          clickReject,
          rejectedTab,
          pageIndex,
          pageSize
        )
      ).then((res) => {
        console.log("fetchSentencesForTranslator response:", res);
        const data = res.payload?.data || []; // Adjust based on actual response structure
        if (Array.isArray(data)) {
          setHasNextPage(data.length >= pageSize);
        } else {
          setHasNextPage(false);
        }
      });
    } else {
      console.warn("🚫 Skipping fetch — fileLanguage values not ready");
    }
  }, [fileLanguage, translatorUserId, dispatch, clickReject, rejectedTab, reloadTrigger, pageIndex, pageSize]);


  const paginatedSentences = useMemo(() => {
    console.log("paginatedSentences:", sentences);
    return sentences || [];
  }, [sentences]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedSentences([]);
    } else {
      setSelectedSentences(paginatedSentences?.map((sentence) => sentence.sentenceId) || []);
    }
    setSelectAll(!selectAll);
  };

  const handleAssignClick = ({ row }) => {
    dispatch(assignSentences([row.sentenceId], fileLanguage.fileId));
  };

  const handleBulkAssign = () => {
    if (selectedSentences.length === 0) {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Please select at least one sentence to assign",
      });
      return;
    }
    dispatch(assignSentences(selectedSentences, fileLanguage.fileId));
  };

  useEffect(() => {
    setSavedStatus({});
  }, [sentences]);

  const allTranslated =
    paginatedSentences &&
    paginatedSentences.length > 0 &&
    paginatedSentences.every((s) => s.translationStatus === "TRANSLATED");

  const handleFinalCommit = () => {
    dispatch(openModal("finalCommitConfirm"));
  };

  const handleConfirmFinalCommit = async () => {
    const { fileId, targetLanguageId } = fileLanguage;
    const sourceLanguageId = 2;
    try {
      const result = await apiClient(`${BASE_URL}api/translatorUser/mark-work-completed`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        credentials: "include",
        body: JSON.stringify({
          fileId,
          sourceLanguageId,
          targetLanguageId,
          translatorUserId,
        }),
      }, navigate);
      if (result.status === "success") {
        setPopup({
          isOpen: true,
          type: "success",
          message: result.message || "Translation work marked as completed!",
        });
        setIsFinalCommitted(true);
        setReloadTrigger((prev) => prev + 1);
      } else {
        setPopup({
          isOpen: true,
          type: "error",
          message: result.message || "Failed to mark work as completed",
        });
      }
    } catch (err) {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Error marking work as completed",
      });
      console.error("Error in final commit:", err);
    } finally {
      dispatch(closeModal("finalCommitConfirm"));
    }
  };

  const handleCancelFinalCommit = () => {
    dispatch(closeModal("finalCommitConfirm"));
  };

  const showRejectSentences = () => {
    setClickReject((prev) => !prev);
    setPageIndex(0); // Reset pageIndex to 0 when toggling
    setSelectedSentences([]); // Clear selected sentences
    setSelectAll(false); // Reset selectAll state
  };

  const saveAllTranslations = async () => {
    console.log("Saving all translations...");
    console.log(translationsRef);
    console.log(sourceRef);
    const payload = sentences.map((s) => {
      const sentenceId = s.sentenceId;
      const editedSourceText = sourceRef.current[sentenceId];
      const editedTranslatedText = translationsRef.current[sentenceId];

      const isSourceEdited =
        editedSourceText !== undefined && editedSourceText !== s.cdacSentenceText;
      const isTranslationEdited =
        editedTranslatedText !== undefined && editedTranslatedText !== s.translatedTextGs;

      if (!isSourceEdited && !isTranslationEdited) {
        return null;
      }

      return {
        fileId: fileLanguage.fileId,
        sentenceId,
        targetLanguageId: fileLanguage.targetLanguageId,
        sentenceText: isSourceEdited ? editedSourceText : s.cdacSentenceText,
        translatedText: isTranslationEdited
          ? editedTranslatedText
          : isSourceEdited
            ? ''
            : s.translatedTextGs,
      };
    }).filter(Boolean);

    try {
      const result = await apiClient(
        `${BASE_URL}api/translatorUser/saveAllTranslate?translatorUserId=${translatorUserId}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(payload),
        },
        navigate
      );
      if (result.status === "success") {
        setPopup({
          isOpen: true,
          type: "success",
          message: "All translations saved successfully!",
        });
        setSavedStatus((prev) => ({
          ...prev,
          ...Object.fromEntries(payload.map((p) => [p.sentenceId, true])),
        }));
        setReloadTrigger((prev) => prev + 1);
      } else {
        setPopup({
          isOpen: true,
          type: "error",
          message: result.message || "Failed to save translations",
        });
      }
    } catch (err) {
      setPopup({
        isOpen: true,
        type: "error",
        message: err.message || "Error saving translations",
      });
      console.error("Error in saveAllTranslations:", err);
    }
  };

const sentenceColumns = useMemo(() => [
  {
    id: "serialNumber",
    header: "Sr. No.",
    cell: ({ row }) => {
      // Calculate continuous serial number across pages
      const serialNumber = pageIndex * pageSize + row.index + 1;
      return (
        <div className="text-center font-['Semantic/Strong']">
          {serialNumber}
        </div>
      );
    },
    cellClassName: "text-center",
  },
  {
  id: "cdacSentenceText",
  header: () => "CDAC Sentence Text",
  size: 420,
  accessorKey: "cdacSentenceText",
  cell: ({ row }) => {
    const sentenceId = row.original.sentenceId;
    const handleChange = (e) => {
      const rawValue = e.target.value;
      const cleanedValue = restrictSpecialCharacters(rawValue);
      if (rawValue !== cleanedValue) {
        setPopup({
          isOpen: true,
          type: "error",
          message:
            "Special characters are not allowed. Use only letters, numbers, spaces, commas, parentheses, and question marks.",
        });
        e.target.blur();
      }
      sourceRef.current[sentenceId] = cleanedValue;
      e.target.value = cleanedValue;
    };

    const sharedClass =
      "w-full min-w-[400px] min-h-[80px] border border-gray-300 rounded-lg p-2 text-sm font-['Semantic/Strong'] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200";

    if (!clickReject && fileStatus !== "SENT_TO_VA" && fileStatus !== "COMPLETED") {
      return row.original.translationStatus !== "TRANSLATED" ? (
        <textarea
          name="translationTextArea"
          className={sharedClass}
          defaultValue={
            sourceRef.current[sentenceId] ?? row.original.cdacSentenceText ?? ""
          }
          onChange={handleChange}
          onPaste={(e) => e.preventDefault()}
        />
      ) : (
        <div className="min-w-[400px] max-w-[400px]">
          {row.original.cdacSentenceText}
        </div>
      );
    }

    return (
      <div className="min-w-[400px] max-w-[400px]">
        {row.original.cdacSentenceText || "-"}
      </div>
    );
  },
},

  clickReject && rejectedTab !== "me" && {
    id: "gaTranslatedText",
    header: () => `GA Translated Text (${targetLanguageName})`,
    accessorKey: "translatedTextGs",
    cell: ({ row }) => {
      const isUnchanged = row.original.translatedTextGs === "No change in CDAC source segment";
      const displayText = isUnchanged ? "No change" : row.original.translatedTextGs || "N/A";
      return (
        <div
          className={`w-full px-4 py-2 text-base font-['Semantic/Strong'] line-clamp-2 hover:line-clamp-none transition-all duration-200 rounded-lg ${isUnchanged ? "text-gray-500 bg-blue-50 hover:bg-blue-100" : "text-gray-900 hover:bg-gray-50"}`}
          title={isUnchanged ? "No modifications made to the CDAC source segment" : row.original.translatedTextGs || "No GA sentence text available"}
          aria-label={isUnchanged ? "No modifications made to the CDAC source segment" : row.original.translatedTextGs || "No GA sentence text available"}
        >
          {displayText}
        </div>
      );
    },
  },
  clickReject && rejectedTab === "me" && {
    id: "gaSentenceText",
    header: () => "GA Sentence Text",
    accessorKey: "gaSentenceText",
    cell: ({ row }) => {
      const isUnchanged = row.original.gaSentenceText === "No change in CDAC source segment";
      const displayText = isUnchanged ? "No change" : row.original.gaSentenceText || "N/A";
      return (
        <div
          className={`max-w-4xl px-4 py-2 text-base font-['Semantic/Strong'] line-clamp-2 hover:line-clamp-none transition-all duration-200 rounded-lg ${isUnchanged ? "text-gray-500 bg-blue-50 hover:bg-blue-100" : "text-gray-900 hover:bg-gray-50"}`}
          title={isUnchanged ? "No modifications made to the CDAC source segment" : row.original.gaSentenceText || "No GA sentence text available"}
          aria-label={isUnchanged ? "No modifications made to the CDAC source segment" : row.original.gaSentenceText || "No GA sentence text available"}
        >
          {displayText}
        </div>
      );
    },
  },
  clickReject && rejectedTab === "me" && {
    id: "rejectionComment",
    header: () => "Rejection Comment",
    accessorKey: "rejectionComment",
    cell: ({ row }) => (
      <div className="max-w-md font-['Semantic/Strong']">
        {row.original.rejectionComment || "-"}
      </div>
    ),
  },
  !(clickReject && rejectedTab === "me") && {
  id: "translatedTextGs",
  header: () => `GA Translated Text (${targetLanguageName})`,
  size: 420,
  accessorKey: "translatedTextGs",
  cell: ({ row }) => {
    const sentenceId = row.original.sentenceId;
    const handleChange = (e) => {
      const rawValue = e.target.value;
      const cleanedValue = restrictSpecialCharactersMultilingual(rawValue);
      if (rawValue !== cleanedValue) {
        setPopup({
          isOpen: true,
          type: "error",
          message:
            "Special characters are not allowed. Use only letters, numbers, spaces, commas, parentheses, and question marks.",
        });
        e.target.blur();
      }
      translationsRef.current[sentenceId] = cleanedValue;
      e.target.value = cleanedValue;
    };

    const sharedClass =
      "w-full min-w-[400px] min-h-[80px] border border-gray-300 rounded-lg p-2 text-sm font-['Semantic/Strong'] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200";

    if (!clickReject && fileStatus !== "SENT_TO_VA" && fileStatus !== "COMPLETED") {
      return row.original.translationStatus !== "TRANSLATED" ? (
        <textarea
          name="translationTextArea"
          className={sharedClass}
          defaultValue={
            translationsRef.current[sentenceId] ?? row.original.translatedTextGs ?? ""
          }
          onChange={handleChange}
          onPaste={(e) => e.preventDefault()}
        />
      ) : (
        <div className="min-w-[400px] max-w-[400px]">
          {row.original.translatedTextGs}
        </div>
      );
    }

    return (
      <div className="min-w-[400px] max-w-[400px]">
        {row.original.translatedTextGs || "-"}
      </div>
    );
  },
  },
  !clickReject && {
    id: "status",
    header: () => "Status",
    accessorKey: clickReject ? "approvalStatus" : "translationStatus",
    cell: ({ row }) => {
      const style = getStatusStyle(clickReject ? row.original.approvalStatus : row.original.translationStatus);
      return (
        <span className={`inline-flex items-center text-center px-2.5 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
          {style.label || "N/A"}
        </span>
      );
    },
    cellClassName: "text-center",
  },
  (!clickReject || rejectedTab !== "me") && {
    id: "action",
    header: () => "Action",
    accessorKey: "save",
    cell: ({ row }) => {
      const sentenceId = row.original.sentenceId;

      const handleCommitClick = () => {
        if (isCommitModalOpen.isOpen) return;
        setIsCommitModalOpen({ isOpen: true, sentenceId });
      };

      return (
        <div className="flex gap-2 justify-center">
          {(() => {
            const isDisabled =
              commitStatus[sentenceId] || row.original.translationStatus === "TRANSLATED";

            return (
              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={handleCommitClick}
                  className={`inline-flex items-center px-3 py-1 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200 ${isDisabled || isCommitModalOpen.isOpen ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={isDisabled || isCommitModalOpen.isOpen}
                >
                  <svg
                    className="w-4 h-4 mr-1.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {isDisabled ? "Committed" : "Commit"}
                </button>
                {!clickReject && (
                  <button
                    onClick={() => {
                      setIsRejectModalOpen(true);
                      setRejectingSentence(row.original);
                      setRejectComment("");
                    }}
                    className={`inline-flex items-center px-3 py-1 border border-red-500 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors duration-200 ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    disabled={isDisabled}
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Reject
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      );
    },
    cellClassName: "text-center",
  },
].filter(Boolean), [clickReject, rejectedTab, fileLanguage, translatorUserId, commitStatus, sourceRef, translationsRef, dispatch, token, pageIndex, pageSize]);

  const isFinalCommitModalOpen = useSelector((state) => state.modal.finalCommitConfirm);

  const renderPopup = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-200">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-[calc(100vw-2rem)] sm:w-4/5 md:w-3/4 max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 z-10">
          <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
            <span className="text-[#ec7026] shadow-sm">{popup.type === "success" ? "Success" : "Error"}</span> Message
          </h2>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="text-sm font-['Semantic/Strong'] text-gray-800">{popup.message}</div>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-4 border-t border-gray-200 bg-white flex justify-end">
          <button
            onClick={() => setPopup({ isOpen: false, type: "", message: "" })}
            className="inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            OK
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex-1">
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <h2 className="text-xl font-['Semantic/Strong'] text-center font-bold">
              File Name: <span className="text-2xl font-['Semantic/Strong'] text-center text-[#ec7026] underline font-bold">{fileName}</span>
            </h2>
            <div className="flex justify-between items-center mb-4">
              <div className="flex flex-wrap gap-2">
                {!isFinalCommitted && fileStatus !== "SENT_TO_VA" && fileStatus !== "COMPLETED" && !clickReject && (
                  <>
                    <button
                      className={`inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200 ${!allTranslated ? "opacity-50 cursor-not-allowed" : ""}`}
                      onClick={handleFinalCommit}
                      disabled={!allTranslated}
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Final Commit
                    </button>
                    <button
                      className="inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                      onClick={saveAllTranslations}
                      disabled={allTranslated}
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                      Save All
                    </button>
                  </>
                )}
              </div>
              <div className="mr-2">
                <button
                  className="inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                  onClick={showRejectSentences}
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={clickReject ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"} />
                  </svg>
                  {clickReject ? "Show Accepted Sentences" : "Show Rejected Sentences"}
                </button>
              </div>
            </div>
            {clickReject && (
              <div className="flex justify-between items-center mb-4">
                <div className="flex border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                  <button
                    className={`px-4 py-2 text-sm font-['Semantic/Strong'] ${rejectedTab === "me" ? "bg-[#ec7026] text-white" : "bg-white text-[#ec7026] hover:bg-gray-50"} transition-colors duration-200`}
                    onClick={() => setRejectedTab("me")}
                  >
                    By Me
                  </button>
                  <button
                    className={`px-4 py-2 text-sm font-['Semantic/Strong'] ${rejectedTab === "validator" ? "bg-[#ec7026] text-white" : "bg-white text-[#ec7026] hover:bg-gray-50"} transition-colors duration-200`}
                    onClick={() => setRejectedTab("validator")}
                  >
                    By Validator
                  </button>
                </div>
              </div>
            )}
            <div className="w-full overflow-x-auto">
              <GenericTable
                columns={sentenceColumns}
                emptyMessage={clickReject ? "No rejected sentences available" : "No approved sentences available"}
                data={paginatedSentences}
                keyField="sentenceId"
                className="mt-4"
                enableRowSelection
                onRowSelectChange={(selectedRows) => {
                  const selectedIds = selectedRows.map((row) => row.sentenceId);
                  setSelectedSentences(selectedIds);
                  setSelectAll(selectedIds.length === paginatedSentences.length);
                }}
                enableSelectAll
                onSelectAllChange={handleSelectAll}
                showPagination={true}
                manualPagination={true}
                pageIndex={pageIndex}
                pageSize={pageSize}
                hasNextPage={hasNextPage}
                onPaginationChange={({ pageIndex, pageSize }) => {
                  setPageIndex(pageIndex);
                  setPageSize(pageSize);
                }}
                renderRowActions={({ row }) => (
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={() => handleAssignClick({ row })}
                      className="inline-flex items-center px-3 py-1 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      Assign
                    </button>
                  </div>
                )}
              />
            </div>
            {selectedSentences.length > 0 && (
              <div className="mt-4">
                <button
                  onClick={handleBulkAssign}
                  className="inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Assign Selected
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-200"
        style={{ display: isRejectModalOpen ? "flex" : "none" }}
      >
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-[calc(100vw-2rem)] sm:w-4/5 md:w-3/4 lg:w-2/3 max-w-xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 z-10">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              <span className="text-[#ec7026] shadow-sm">Reject</span> Sentence
            </h2>
          </div>
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="mb-4 text-gray-800 text-sm font-['Semantic/Strong']">
              Please provide a reason for rejection (words must start with a letter):
            </div>
            <textarea
              className="w-full min-h-[80px] border border-gray-300 rounded-lg p-2 text-sm font-['Semantic/Strong'] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
              value={rejectComment}
              onChange={(e) => {
                const value = e.target.value;
                // Allow only valid input in real-time
                const words = value.split(/\s+/).filter(Boolean);
                const isValid = words.every((word) => /^[a-zA-Z]/.test(word));
                if (isValid || value === "") {
                  setRejectComment(value);
                } else {
                  setPopup({
                    isOpen: true,
                    type: "error",
                    message: "Each word must start with a letter (a-z or A-Z).",
                  });
                }
              }}
              placeholder="Enter rejection comment..."
            />
          </div>
          <div className="px-4 sm:px-6 lg:px-8 py-4 border-t border-gray-200 bg-white flex justify-end gap-2">
            <button
              onClick={() => setIsRejectModalOpen(false)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </button>
            <button
              onClick={async () => {
                if (!rejectComment.trim()) {
                  setPopup({
                    isOpen: true,
                    type: "error",
                    message: "Please enter a comment for rejection",
                  });
                  return;
                }
                // Validate that each word starts with a letter
                const words = rejectComment.trim().split(/\s+/).filter(Boolean);
                if (!words.every((word) => /^[a-zA-Z]/.test(word))) {
                  setPopup({
                    isOpen: true,
                    type: "error",
                    message: "Each word must start with a letter (a-z or A-Z).",
                  });
                  return;
                }
                if (!rejectingSentence) return;
                console.log(rejectingSentence)
                try {
                  const payload = { comment: rejectComment };
                  const result = await apiClient(
                    `${BASE_URL}api/translatorUser/rejectSentence?sentenceId=${rejectingSentence.sentenceId}&translatorUserId=${translatorUserId}&fileId=${rejectingSentence.fileId}&targetLanguageId=${rejectingSentence.targetLanguageId}`,
                    {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                      },
                      credentials: "include",
                      body: JSON.stringify(payload),
                    }, navigate
                  );
                  if (result.success) {
                    setPopup({
                      isOpen: true,
                      type: "success",
                      message: "Sentence rejected successfully!",
                    });
                    setIsRejectModalOpen(false);
                    setRejectComment("");
                    setRejectingSentence(null);
                    setReloadTrigger((prev) => prev + 1); // Trigger useEffect to refetch data
                  } else {
                    setPopup({
                      isOpen: true,
                      type: "error",
                      message: result.message || "Failed to reject sentence",
                    });
                  }
                } catch (err) {
                  setPopup({
                    isOpen: true,
                    type: "error",
                    message: err.message,
                  });
                }
              }}
              className="inline-flex items-center px-3 py-2 border border-red-500 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Submit Rejection
            </button>
          </div>
        </div>
      </div>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-200"
        style={{ display: isFinalCommitModalOpen ? "flex" : "none" }}
      >
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-[calc(100vw-2rem)] sm:w-4/5 md:w-3/4 lg:w-2/3 max-w-xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 z-10">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              <span className="text-[#ec7026] shadow-sm">Final</span> Commit Confirmation
            </h2>
            <p className="text-sm text-gray-500 font-['Semantic/Strong'] mt-1">
              Confirm submission of all translated sentences
            </p>
          </div>
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="mb-4 text-gray-800 text-sm font-['Semantic/Strong']">
              Are you sure you want to final commit all translated sentences? This action cannot be undone.
            </div>
          </div>
          <div className="px-4 sm:px-6 lg:px-8 py-4 border-t border-gray-200 bg-white flex justify-end gap-2">
            <button
              onClick={handleCancelFinalCommit}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </button>
            <button
              onClick={handleConfirmFinalCommit}
              className="inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              Yes, Final Commit
            </button>
          </div>
        </div>
      </div>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-200"
        style={{ display: isCommitModalOpen.isOpen ? "flex" : "none" }}
      >
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-[calc(100vw-2rem)] sm:w-4/5 md:w-3/4 max-w-md max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 z-10">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              <span className="text-[#ec7026] shadow-sm">Confirm</span> Commit
            </h2>
          </div>
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="mb-4 text-gray-800 text-sm font-['Semantic/Strong']">
              Are you sure you want to commit this translation?
            </div>
          </div>
          <div className="px-4 sm:px-6 lg:px-8 py-4 border-t border-gray-200 bg-white flex justify-end gap-2">
            <button
              onClick={() => setIsCommitModalOpen({ isOpen: false, sentenceId: null })}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </button>
            <button
              onClick={() => {
                const sentence = sentences.find((s) => s.sentenceId === isCommitModalOpen.sentenceId);
                if (sentence) {
                  console.log("Committing sentence:", sentence);
                  const handleSubmitCommit = async () => {
                    // FIX: Use edited source from ref, and apply consistent logic for translated text (matching saveAllTranslations)
                    const editedSource = sourceRef.current[isCommitModalOpen.sentenceId] || sentence.cdacSentenceText;
                    const editedTranslation = translationsRef.current[isCommitModalOpen.sentenceId] ?? sentence.translatedTextGs ?? "";
                    const isSourceEdited = editedSource !== sentence.cdacSentenceText;
                    const isTranslationEdited = editedTranslation !== sentence.translatedTextGs;

                    const translatedTextToSend = isTranslationEdited
                      ? editedTranslation
                      : isSourceEdited
                        ? ""  // Clear translation if source edited but translation not (consistent with saveAll)
                        : sentence.translatedTextGs;

                    const payload = {
                      fileId: fileLanguage.fileId,
                      sentenceId: isCommitModalOpen.sentenceId,
                      sentenceText: editedSource ,
                      targetLanguageId: fileLanguage.targetLanguageId,
                      translatedText: translatedTextToSend,
                    };

                    // OPTIONAL FIX: Prevent commit if translated text would be empty (adjust or remove based on your requirements)
                    if (!payload.translatedText.trim()) {
                      setPopup({
                        isOpen: true,
                        type: "error",
                        message: "Cannot commit: Translation text is empty. Please provide or update the translation.",
                      });
                      setIsCommitModalOpen({ isOpen: false, sentenceId: null });
                      return;
                    }

                    try {
                      const result = await apiClient(
                        `${BASE_URL}api/translatorUser/commitTranslate?translatorUserId=${translatorUserId}`,
                        {
                          method: "PATCH",
                          headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                          },
                          credentials: "include",
                          body: JSON.stringify(payload),
                        },
                        navigate
                      );
                      if (result.status === "success") {
                        toast.success("Translation committed successfully!");
                        setCommitStatus((prev) => ({ ...prev, [isCommitModalOpen.sentenceId]: true }));
                        setReloadTrigger((prev) => prev + 1);
                      } else {
                        toast.error(err.message || "Error while committing translation");
                      }
                    } catch (err) {
                      setPopup({
                        isOpen: true,
                        type: "error",
                        message: err.message,
                      });
                      console.error("Error in commit:", err);
                    } finally {
                      setIsCommitModalOpen({ isOpen: false, sentenceId: null });
                    }
                  };
                  handleSubmitCommit();
                }
              }}
              className="inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              Yes, Commit
            </button>
          </div>
        </div>
      </div>
      {popup.isOpen && renderPopup()}
    </div>
  );
};

export default SentenceTranslationTable;